import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Theme } from "../../theme";

// アニメーション
const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const FooterContainer = styled.footer<{ theme: Theme }>`
	margin-top: 2rem;
	padding: 1.5rem 0 1rem;
	border-top: 1px solid ${props => props.theme.colors.neutral[200]};
	animation: ${fadeIn} 0.8s ease-out;
`;

const FooterContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
`;

const FooterLegalLinks = styled.div`
	width: 100%;
`;

const FooterLegalList = styled.ul`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 1rem;
	list-style: none;
	margin: 0;
	padding: 0;
	flex-wrap: wrap;

	@media (max-width: 640px) {
		gap: 1rem;
	}
`;

const FooterLegalItem = styled.li<{ theme: Theme }>`
	a {
		color: ${props => props.theme.colors.text.tertiary};
		font-size: 0.875rem;
		text-decoration: none;
		transition: color 0.3s ease;

		&:hover {
			color: ${props => props.theme.colors.primary.main};
		}

		@media (max-width: 640px) {
			font-size: 0.8rem;
		}
	}
`;

const FooterCredits = styled.div<{ theme: Theme }>`
	text-align: center;
	padding-top: 1rem;
	border-top: 1px solid ${props => props.theme.colors.neutral[200]};
	width: 100%;
`;

const FooterCreditsText = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.95rem;
	font-weight: 400;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	flex-wrap: wrap;

	&::before,
	&::after {
		content: '';
		width: 50px;
		height: 1px;
		background: linear-gradient(
			to right,
			transparent,
			${props => props.theme.colors.neutral[400]},
			transparent
		);
	}

	@media (max-width: 640px) {
		font-size: 0.85rem;

		&::before,
		&::after {
			width: 30px;
		}
	}
`;

const Footer = () => {
	const currentYear = new Date().getFullYear();
	const { theme } = useTheme();
	const { t } = useLanguage();

	return (
		<FooterContainer theme={theme}>
			<FooterContent>
				<FooterLegalLinks>
					<FooterLegalList>
						<FooterLegalItem theme={theme}>
							<Link to="/legal/privacy-policy">{t.nav.privacyPolicy}</Link>
						</FooterLegalItem>
						<FooterLegalItem theme={theme}>
							<Link to="/legal/terms-of-service">{t.nav.termsOfService}</Link>
						</FooterLegalItem>
						<FooterLegalItem theme={theme}>
							<Link to="/legal/commercial-law">{t.nav.commercialLaw}</Link>
						</FooterLegalItem>
						<FooterLegalItem theme={theme}>
							<Link to="/legal/disclaimer">{t.nav.disclaimer}</Link>
						</FooterLegalItem>
					</FooterLegalList>
				</FooterLegalLinks>

				<FooterCredits theme={theme}>
					<FooterCreditsText theme={theme}>
						© {currentYear} Shimomura Kazumasa
					</FooterCreditsText>
				</FooterCredits>
			</FooterContent>
		</FooterContainer>
	);
};

export default Footer;
